import { useEffect, useRef } from 'react';
import { telemetryCountBucket, telemetryStuckThresholds } from '../shared/telemetry-catalog';
import {
  canceledByStop,
  setupFailureCategory,
  stoppedByPerson,
  type SetupFailureCategory,
} from './setup-failure';
import {
  advanceNoProgress,
  advanceSetupStage,
  type NoProgressWatch,
  type SetupHandoffHow,
  type SetupStageCode,
  type SetupStageWatch,
} from './setup-telemetry';
import { reportEvent } from './telemetry';

/**
 * Trial telemetry from the setup screens (telemetry catalog §5.6): the stage each job's setup shows and how long
 * each took, the stage a stop or a failure happened in, a wait whose counts stopped moving, the job ready, and how
 * the wizard handed over. Fire-and-forget through reportEvent; nothing here changes what a screen shows.
 */

/**
 * The stage each job's setup is timed in, with the most pages it has shown, for this window's life: a screen that
 * remounts (Resume, the wizard reopened over the job) carries on timing the same stage.
 */
const stageWatches = new Map<string, NonNullable<SetupStageWatch> & { pages: number }>();

const sendStage = (jobId: string, stage: SetupStageCode, ms: number, pages: number) =>
  reportEvent(`setup.stage:${stage}`, { jobId, durationMs: ms, attrs: { ms, pages } });

/** The setup stage a job's screen shows now: the stage it moves on from is sent with its time and pages. */
export function noteSetupStage(
  jobId: string,
  stage: SetupStageCode,
  pages: number,
  now = Date.now(),
): void {
  const previous = stageWatches.get(jobId) ?? null;
  const { watch, finished } = advanceSetupStage(previous, stage, now);
  if (finished && previous)
    sendStage(jobId, finished.stage, finished.ms, Math.max(previous.pages, pages));
  if (watch)
    stageWatches.set(jobId, {
      ...watch,
      pages: finished || !previous ? pages : Math.max(previous.pages, pages),
    });
}

/** Setup's last stage is over (the job handed to the person, or a first scan's documents in): it is sent too. */
export function finishSetupStages(jobId: string, now = Date.now()): void {
  const watch = stageWatches.get(jobId);
  if (!watch) return;
  stageWatches.delete(jobId);
  sendStage(jobId, watch.stage, Math.max(0, now - watch.since), watch.pages);
}

/** The stage a job's setup screen last showed, for a stop or a failure. */
export const setupStageNow = (jobId: string): SetupStageCode | 'unknown' =>
  stageWatches.get(jobId)?.stage ?? 'unknown';

/** Times the stage a setup screen shows; null while it shows none (a first scan's pages read, then done). */
export function useSetupStageTelemetry(
  jobId: string,
  stage: SetupStageCode | null,
  pages: number,
): void {
  const timing = useRef(false);
  useEffect(() => {
    if (stage) {
      timing.current = true;
      noteSetupStage(jobId, stage, pages);
    } else if (timing.current) {
      timing.current = false;
      finishSetupStages(jobId);
    }
  }, [jobId, stage, pages]);
}

/**
 * `reading.no-progress` once when the screen's counts (`key`) have stood still for three visible minutes while it
 * waits (telemetryStuckThresholds.noProgressMs), and again only after they move. `now` is the screen's own
 * one-second clock (useWorkingProgress): no timer of its own.
 */
export function useNoProgressTelemetry(
  jobId: string,
  waiting: boolean,
  key: string,
  now: number,
  stage: SetupStageCode | 'importing' | 'unknown',
): void {
  const watch = useRef<NoProgressWatch | null>(null);
  useEffect(() => {
    if (!waiting) {
      watch.current = null;
      return;
    }
    const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
    const next = advanceNoProgress(
      watch.current,
      `${jobId}|${key}`,
      now,
      visible,
      telemetryStuckThresholds.noProgressMs,
    );
    watch.current = next.watch;
    if (next.minutes !== null)
      reportEvent('reading.no-progress', { jobId, attrs: { stage, minutes: next.minutes } });
  }, [jobId, waiting, key, now, stage]);
}

/** The person stopped setup: the stage it showed and how long it had run. */
export function reportSetupStopped(jobId: string, ms: number): void {
  const stage = setupStageNow(jobId);
  stageWatches.delete(jobId);
  reportEvent('setup.stopped', { jobId, attrs: { stage, ms: Math.max(0, Math.round(ms)) } });
}

/** A setup failed in front of the person: why, as a category, and the stage it showed. */
export function reportSetupFailed(jobId: string, category: SetupFailureCategory): void {
  const stage = setupStageNow(jobId);
  stageWatches.delete(jobId);
  reportEvent(`setup.failed:${category}`, { jobId, attrs: { stage } });
}

/**
 * A setup, resume or Read more that ended in an error: sent as `setup.failed` by category, unless it was the
 * person's own stop (never a failure). True when it was a failure.
 */
export function reportSetupFailure(
  jobId: string,
  raw: string,
  savedFailure?: string | null,
): boolean {
  if (canceledByStop(raw) || stoppedByPerson(raw) || stoppedByPerson(savedFailure)) return false;
  reportSetupFailed(jobId, setupFailureCategory(raw));
  return true;
}

/** The job is set up and the person's to use: how long since it was made, its documents and its types. */
export function reportSetupReady(
  job: { id: string; createdAt: string },
  documents: number,
  typesFound: number,
): void {
  const created = Date.parse(job.createdAt);
  reportEvent('setup.ready', {
    jobId: job.id,
    attrs: {
      ...(Number.isFinite(created) ? { ms_since_created: Math.max(0, Date.now() - created) } : {}),
      documents: telemetryCountBucket(documents),
      types_found: typesFound,
    },
  });
}

/** Jobs whose page sources were shown with no scanner this window's life, so each is said once. */
const noScannerJobs = new Set<string>();

/**
 * Scan paper is shown unavailable while the person chooses a source: no scanner was found on this computer. A
 * source outcome, not a hurdle: most people bring in files and never meant to scan, so it is not a message code
 * (which Phone Home counts as the last hurdle the person hit).
 */
export function reportNoScanner(jobId: string): void {
  if (noScannerJobs.has(jobId)) return;
  noScannerJobs.add(jobId);
  reportEvent('source.outcome:no-scanner', { jobId, attrs: { files: '0' } });
}

/** The wizard handed over to the job: how, and how long its documents had been read when it did. */
export function reportSetupHandoff(jobId: string, how: SetupHandoffHow, ms: number): void {
  finishSetupStages(jobId);
  reportEvent(`setup.handoff:${how}`, { jobId, attrs: { ms: Math.max(0, Math.round(ms)) } });
}
